// DelForm.js
// This will be one of the main forms that the user will fill out in this application. It has a Project, Employee, Units, Field Notes, JSA Tab, and a Waste Disposal tab for the user to fill out. All of this will be managed in the global state under activeDel.reducer.js

import React, { useState } from "react";
import { shallow } from "zustand/shallow";
import { useNavigate } from "react-router-dom";
// State
import { useAppStore } from "../../store/appStore";
// Autosave Hook
import { useDelAutosave } from "../../hooks/useDelAutosave";
// User
import { useUserState } from "../../context/user";
// Components
import { ProjectTab } from "./components/projects/projectComponents";
import { EmployeeTab } from "./components/employees/employeeComponents";
import { UnitTabs } from "./components/units/unitComponents";
import { FieldNotesTab } from "./components/field_notes/fieldNotesComponents";
import { JsaTab } from "../jsaForm/JsaTab.js";
import { MiscTab } from "./components/misc/MiscTab";
import {
  BackToTableButton,
  SubmitDelButton,
  ShowAllButton,
  CopyDelButton,
  ApproveDelButton,
  RejectDelButton,
  UnapproveDelButton,
} from "../../features/landingPage/components/buttons/DelActions";
// Modals
import RejectDelModal from "../../features/landingPage/components/modal/RejectDelModal";
import ConfirmationModal from "../common/ConfirmationModal";
// Autosave Indicator
import { AutosaveIndicator } from "../common/AutosaveIndicator";
// Styles
import styles from "./DelForm.module.css";

// A component to show all the tabs in a single view.
const ShowAllTabsView = ({ delTabProps, actionFunctions }) => {
  return (
    <div className={styles.showAllTabsContainer}>
      <div className={styles.showAllTabs}>
        <div className={styles.tabSection}>
          <h2 className={styles.sectionTitle}>Project Information</h2>
          <ProjectTab
            projectProps={delTabProps}
            projectActions={actionFunctions.delFormActions.projects}
            clientSignaturesActions={
              actionFunctions.delFormActions.clientSignatures
            }
          />
        </div>

        <div className={styles.tabSection}>
          <h2 className={styles.sectionTitle}>Employee Information</h2>
          <EmployeeTab
            employeeProps={delTabProps}
            actions={actionFunctions.delFormActions.employees}
          />
        </div>

        <div className={styles.tabSection}>
          <h2 className={styles.sectionTitle}>Units & Equipment</h2>
          <UnitTabs
            unitProps={delTabProps}
            actions={actionFunctions.delFormActions.units}
          />
        </div>

        <div className={styles.tabSection}>
          <h2 className={styles.sectionTitle}>Field Notes</h2>
          <FieldNotesTab
            fieldNotesProps={delTabProps}
            actions={actionFunctions.delFormActions.fieldNotes}
          />
        </div>

        {delTabProps.projectUI.showJsaTab && (
          <div className={styles.tabSection}>
            <h2 className={styles.sectionTitle}>Job Safety Analysis</h2>
            <JsaTab
              jsaProps={delTabProps}
              // We need to specify which actions we are sending here.
              actions={actionFunctions.delFormActions.jsa}
            />
          </div>
        )}

        <div className={`${styles.tabSection} ${styles.miscShowAllSection}`}>
          <h2 className={styles.sectionTitle}>Misc</h2>
          <MiscTab
            miscProps={delTabProps}
            actions={actionFunctions.delFormActions}
          />
        </div>
      </div>
    </div>
  );
};

const DelForm = ({
  activeDelTabInfo, // This is the activeDelInitialState .tab that is passed from the Tab that the form is opened from
  actionFunctions, // This is the actionFunctions that is passed from the Tab that the form is opened from
  setShowDelForm, // Function to hide the form
  tableName, // Tab name for navigation
  copyDelFunction = null,
  refreshDelListFunction = null,
  unapproveDelOption = false,
}) => {
  // Navigation
  const navigate = useNavigate();
  const navigateToTab = useAppStore((s) => s.navigationActions.navigateToTab);
  // State - App Store
  const currentUser = useUserState().currentUser;
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  const allEmployees = useAppStore((s) => s.employees.list, shallow);
  const allEmployeePositions = useAppStore(
    (s) => s.employeePositions.list,
    shallow,
  );
  const allUnits = useAppStore((s) => s.units.list, shallow);

  // Loading
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);

  // State and functions- activeDel
  // Read revision from the active tab info prop (works for drafts, submits, and approvals)
  const rev = activeDelTabInfo?.rev;
  const { submitDel, approveDel, rejectDel, unapproveDel } = useAppStore(
    (s) => s.delWorkflowActions,
  );

  // WebSocket autosave - only enabled for new/edit modes
  const isAutosaveEnabled =
    activeDelTabInfo?.delMode === "new" ||
    activeDelTabInfo?.delMode === "edit" ||
    activeDelTabInfo?.delMode === "approve";

  // Get autosave error actions from the action functions
  const autosaveErrorsActions = actionFunctions?.delFormActions?.autosaveErrors;

  // Get autosave errors state from the form state
  const autosaveErrors = activeDelTabInfo?.delForm?.autosaveErrors || {
    hasErrors: false,
    fieldErrors: {},
    rowErrors: {},
    lastError: null,
  };

  const {
    emitPatch,
    connectionStatus,
    saveStatus,
    pendingCount,
    lastError,
    hasPendingPatches,
  } = useDelAutosave(
    isAutosaveEnabled ? activeDelTabInfo?.currentDel?.id : null,
    autosaveErrorsActions,
  );

  // Active Tab
  const { hideNotification, showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow,
  );

  // State to show the reject del modal
  const [showRejectDelModal, setShowRejectDelModal] = useState(false);

  // State for unsaved photos confirmation modal
  const [showUnsavedPhotosConfirm, setShowUnsavedPhotosConfirm] =
    useState(false);
  const [pendingSubmitAction, setPendingSubmitAction] = useState(null); // "submit" | "approve"

  // Guard: if currentDel is null (e.g., fetch failed while form was still visible),
  // bail out early to prevent crashes accessing null properties.
  // Note: This guard must come AFTER all hooks to satisfy React's rules of hooks.
  if (!activeDelTabInfo?.currentDel) {
    return null;
  }

  // Back to table functions
  const backToTableFunction = () => {
    navigateToTab(navigate, tableName);
    setShowDelForm();
    refreshDelListFunction?.();
  };

  // Handle showing the All Tabs view
  const handleShowAllTabs = () => {
    actionFunctions.setShowAll(!activeDelTabInfo.delShowAll);
  };

  const handleChangeTabs = (tabName) => {
    actionFunctions.setActiveTab(tabName);
  };

  // Core submit logic (called directly or after unsaved photos confirmation)
  const executeSubmitDel = async () => {
    showLoading("Submitting DEL...");
    const res = await submitDel(
      activeDelTabInfo.currentDel,
      activeDelTabInfo.delMode,
      currentUser,
    );
    hideLoading();
    if (res.ok) {
      refreshDelListFunction?.();
      showNotification({
        type: "success",
        title: "DEL Submitted",
        message: "Your DEL was submitted successfully.",
      });
      setShowDelForm(false);
    } else if (res.delErrors) {
      showNotification({
        type: "validation",
        title: "DEL Validation Errors",
        errors: res.delErrors,
        displayMessages: res.displayMessages,
      });
    }
  };

  // Core approve logic (called directly or after unsaved photos confirmation)
  const executeApproveDel = async () => {
    showLoading("Approving DEL...");
    const res = await approveDel(
      activeDelTabInfo.currentDel,
      activeDelTabInfo.delMode,
      currentUser,
    );
    hideLoading();
    if (res.ok) {
      refreshDelListFunction?.();
      showNotification({
        type: "success",
        title: "DEL Approved",
        message: "Your DEL was approved successfully.",
      });
      setShowDelForm(false);
    } else if (res.delErrors) {
      showNotification({
        type: "validation",
        title: "DEL Validation Errors",
        errors: res.delErrors,
        displayMessages: res.displayMessages,
      });
    }
  };

  // Unsaved photos confirmation handler
  const handleUnsavedPhotosConfirm = async () => {
    setShowUnsavedPhotosConfirm(false);
    if (pendingSubmitAction === "submit") {
      await executeSubmitDel();
    } else if (pendingSubmitAction === "approve") {
      await executeApproveDel();
    }
    setPendingSubmitAction(null);
  };

  // Submit DEL Button Function
  const submitDelButtonFunction = async () => {
    if (hasPendingPatches && hasPendingPatches()) {
      showNotification({
        type: "warning",
        title: "Saving in Progress",
        message:
          "Please wait for all changes to finish saving before submitting.",
      });
      return;
    }
    if (pendingCount > 0) {
      showNotification({
        type: "warning",
        title: "Unsaved Changes",
        message:
          "There are changes waiting to be saved. Please ensure you have a stable connection before submitting.",
      });
      return;
    }
    if (autosaveErrors?.hasErrors) {
      showNotification({
        type: "warning",
        title: "Save Errors",
        message:
          "Some changes could not be saved. Please review the errors before submitting.",
      });
      return;
    }

    // Check for unsaved selected photos (last guard)
    const selectedPhotos =
      activeDelTabInfo.delForm.photosUI?.selectedPhotos || [];
    if (selectedPhotos.length > 0) {
      setPendingSubmitAction("submit");
      setShowUnsavedPhotosConfirm(true);
      return;
    }

    await executeSubmitDel();
  };

  // Approve DEL Button Function
  const approveDelButtonFunction = async () => {
    if (hasPendingPatches && hasPendingPatches()) {
      showNotification({
        type: "warning",
        title: "Saving in Progress",
        message:
          "Please wait for all changes to finish saving before approving.",
      });
      return;
    }
    if (pendingCount > 0) {
      showNotification({
        type: "warning",
        title: "Unsaved Changes",
        message:
          "There are changes waiting to be saved. Please ensure you have a stable connection before approving.",
      });
      return;
    }
    if (autosaveErrors?.hasErrors) {
      showNotification({
        type: "warning",
        title: "Save Errors",
        message:
          "Some changes could not be saved. Please review the errors before approving.",
      });
      return;
    }

    // Check for unsaved selected photos (last guard)
    const selectedPhotos =
      activeDelTabInfo.delForm.photosUI?.selectedPhotos || [];
    if (selectedPhotos.length > 0) {
      setPendingSubmitAction("approve");
      setShowUnsavedPhotosConfirm(true);
      return;
    }

    await executeApproveDel();
  };

  // Reject DEL Button Function
  const rejectDelModalFunction = async (reason) => {
    setShowRejectDelModal(false);
    showLoading("Rejecting DEL...");
    const res = await rejectDel(
      activeDelTabInfo.currentDel.id,
      reason,
      currentUser,
    );
    hideLoading();
    if (res.ok) {
      refreshDelListFunction?.();
      setShowDelForm(false);
    } else if (res.delErrors) {
      showNotification({
        type: "error",
        title: "Error Rejecting DEL",
        errors: res.delErrors,
      });
    } else {
      showNotification({
        type: "error",
        title: "Error Rejecting DEL",
        message:
          "A network error occurred while rejecting your DEL. Please try again.",
      });
    }
  };

  // Unapprove DEL Button Function
  const unapproveDelButtonFunction = async () => {
    showLoading("Unapproving DEL...");
    const res = await unapproveDel(activeDelTabInfo.currentDel.id, currentUser);
    hideLoading();
    if (res.ok) {
      refreshDelListFunction?.();
      setShowDelForm(false);
      showNotification({
        type: "success",
        title: "DEL Unapproved",
        message: "Your DEL was unapproved successfully.",
      });
    } else if (res.delErrors) {
      showNotification({
        type: "error",
        title: "Error Unapproving DEL",
        message: res.delErrors,
      });
    } else {
      showNotification({
        type: "error",
        title: "Error Unapproving DEL",
        message:
          "A network error occurred while unapproving your DEL. Please try again.",
      });
    }
  };

  // Reject DEL Button Function
  const rejectDelButtonFunction = async () => {
    // Showing the Reject DEL Modal
    setShowRejectDelModal(true);
  };

  // Debug logs
  // console.log("allProjectInfo", allProjectInfo);
  // console.log("activeDelTabInfo", activeDelTabInfo);
  // console.log("actionFunctions", actionFunctions);
  // console.log("delErrors", activeDelTabInfo.delErrors);
  // console.log("Del: ", activeDelTabInfo.currentDel);

  // Setting up properties to pass to all the tabs
  const delTabProps = {
    delData: activeDelTabInfo.currentDel,
    delMode: activeDelTabInfo.delMode,
    allProjectInfo: allProjectInfo,
    allEmployees: allEmployees,
    allEmployeePositions: allEmployeePositions.filter(
      (position) => position.status === 1,
    ),
    allUnits: allUnits,
    delErrors: activeDelTabInfo.delErrors,
    tabName: activeDelTabInfo.tabName,
    projectUI: activeDelTabInfo.delForm.projectUI,
    employeeUI: activeDelTabInfo.delForm.employeeUI,
    unitsUI: activeDelTabInfo.delForm.unitsUI,
    fieldNotesUI: activeDelTabInfo.delForm.fieldNotesUI,
    clientSignaturesUI: activeDelTabInfo.delForm.clientSignaturesUI,
    showJsaTab: activeDelTabInfo.delForm.projectUI.showJsaTab,
    showWasteDisposalTab:
      activeDelTabInfo.delForm.projectUI.showWasteDisposalTab,
    showAll: activeDelTabInfo.delShowAll,
    // JSA Form Data
    jsaData: activeDelTabInfo.currentDel.jsa,
    jsaVersion: activeDelTabInfo.currentDel.projectInfo.jsaVersion,
    jsaMode: activeDelTabInfo.delMode,
    jsaBase: activeDelTabInfo.delForm.jsaFormUI.base,
    jsaCore: activeDelTabInfo.delForm.jsaFormUI.core,
    jsaV1: activeDelTabInfo.delForm.jsaFormUI.v1,
    jsaV2: activeDelTabInfo.delForm.jsaFormUI.v2,
    delJsaEmployeeList: activeDelTabInfo.currentDel.employees,
    // Waste Disposal Form Data
    wdData: activeDelTabInfo.currentDel.wasteDisposal,
    wdMode: activeDelTabInfo.delMode,
    wdBase: activeDelTabInfo.delForm.wasteDisposalFormUI,
    // Misc Tab UI State
    miscUI: activeDelTabInfo.delForm.miscUI,
    // Photos UI State
    photosUI: activeDelTabInfo.delForm.photosUI,
    // WebSocket autosave - passed down to all tabs
    emitPatch: isAutosaveEnabled ? emitPatch : null,
    connectionStatus: connectionStatus,
    // Autosave field errors for inline error display
    autosaveErrors: autosaveErrors,
    // Loading and notification functions for photos
    showLoading: showLoading,
    hideLoading: hideLoading,
    showNotification: showNotification,
    // Flag for JSA to wrap patches in DEL format
    isEmbeddedInForm: true,
  };

  return (
    <div className={styles.delFormContainer}>
      {showRejectDelModal && (
        <RejectDelModal
          onRejectFunction={(reason) => {
            return rejectDelModalFunction(reason);
          }}
          onCancel={() => setShowRejectDelModal(false)}
          afterReject={() => setShowDelForm(false)}
        />
      )}

      {/* Unsaved photos confirmation modal */}
      <ConfirmationModal
        isOpen={showUnsavedPhotosConfirm}
        onCancel={() => {
          setShowUnsavedPhotosConfirm(false);
          setPendingSubmitAction(null);
        }}
        onConfirm={handleUnsavedPhotosConfirm}
        title="Unsaved Photos"
        message={`You have ${activeDelTabInfo.delForm.photosUI?.selectedPhotos?.length || 0} selected photo(s) that have not been saved. If you continue, these photos will not be included.`}
        confirmText="Continue Without Photos"
        cancelText="Go Back"
      />
      {activeDelTabInfo.delShowAll ? (
        <ShowAllTabsView
          delTabProps={delTabProps}
          actionFunctions={actionFunctions}
        />
      ) : (
        <>
          {/* Del Tabs Container */}
          <div className={styles.delTabMenuBar}>
            <button
              className={`${styles.delTabButton} ${
                activeDelTabInfo.delActiveTab === "projects"
                  ? styles.active
                  : ""
              }`}
              onClick={() => handleChangeTabs("projects")}
            >
              Project Info
            </button>
            <button
              className={`${styles.delTabButton} ${
                activeDelTabInfo.delActiveTab === "employee"
                  ? styles.active
                  : ""
              }`}
              onClick={() => handleChangeTabs("employee")}
            >
              Employees
            </button>
            <button
              className={`${styles.delTabButton} ${
                activeDelTabInfo.delActiveTab === "units" ? styles.active : ""
              }`}
              onClick={() => handleChangeTabs("units")}
            >
              Units
            </button>
            <button
              className={`${styles.delTabButton} ${
                activeDelTabInfo.delActiveTab === "fieldNotes"
                  ? styles.active
                  : ""
              }`}
              onClick={() => handleChangeTabs("fieldNotes")}
            >
              Field Notes
            </button>
            {delTabProps.showJsaTab && (
              <button
                className={`${styles.delTabButton} ${
                  activeDelTabInfo.delActiveTab === "jsa" ? styles.active : ""
                }`}
                onClick={() => handleChangeTabs("jsa")}
              >
                JSA
              </button>
            )}
            <button
              className={`${styles.delTabButton} ${
                activeDelTabInfo.delActiveTab === "misc" ? styles.active : ""
              }`}
              onClick={() => handleChangeTabs("misc")}
            >
              Misc
            </button>
          </div>
          {/* Del Tab Content */}
          <div className={styles.delTabContent}>
            {(() => {
              switch (activeDelTabInfo.delActiveTab) {
                case "projects":
                  return (
                    <ProjectTab
                      projectProps={delTabProps}
                      tabProps={{
                        setHideJsaTab: actionFunctions.setJsaTab,
                        setHideWasteDisposalTab:
                          actionFunctions.setWasteDisposalTab,
                      }}
                      projectActions={actionFunctions.delFormActions.projects}
                      clientSignaturesActions={
                        actionFunctions.delFormActions.clientSignatures
                      }
                    />
                  );
                case "employee":
                  return (
                    <EmployeeTab
                      employeeProps={delTabProps}
                      actions={actionFunctions.delFormActions.employees}
                    />
                  );
                case "units":
                  return (
                    <UnitTabs
                      unitProps={delTabProps}
                      actions={actionFunctions.delFormActions.units}
                    />
                  );
                case "fieldNotes":
                  return (
                    <FieldNotesTab
                      fieldNotesProps={delTabProps}
                      actions={actionFunctions.delFormActions.fieldNotes}
                    />
                  );
                case "jsa":
                  return (
                    <JsaTab
                      jsaProps={delTabProps}
                      // We need to specify which actions we are sending here.
                      actions={actionFunctions.delFormActions.jsa}
                    />
                  );
                case "misc":
                  return (
                    <MiscTab
                      miscProps={delTabProps}
                      actions={actionFunctions.delFormActions}
                    />
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </>
      )}
      {/* Back to table button, only showing if delMode is not new */}
      <div className={styles.delActionButtonsContainer}>
        <div className={styles.rightSideTableActions}>
          <BackToTableButton
            backToTableFunction={backToTableFunction}
            tableName={activeDelTabInfo.tabName}
          />
          {/* Setting a Show All button to show all the DEL tab components in a single view */}
          <ShowAllButton
            showAllFunction={handleShowAllTabs}
            showAll={activeDelTabInfo.delShowAll}
          />
          {/* Autosave indicator - only show in new/edit modes */}
          {isAutosaveEnabled && (
            <AutosaveIndicator
              connectionStatus={connectionStatus}
              saveStatus={saveStatus}
              pendingCount={pendingCount}
              lastError={lastError}
              onErrorClick={(error) => {
                // Show error details in notification
                showNotification({
                  type: "error",
                  title: error.summary || "Autosave Error",
                  message: error.field_errors
                    ? Object.entries(error.field_errors)
                        .map(([field, msg]) => `${field}: ${msg}`)
                        .join("\n")
                    : "An error occurred while saving.",
                });
              }}
            />
          )}
        </div>

        {/* Action Buttons Container */}
        <div className={styles.leftSideTableActions}>
          {activeDelTabInfo.delMode !== "view" &&
            activeDelTabInfo.delMode !== "approve" && (
              <SubmitDelButton submitFunction={submitDelButtonFunction} />
            )}

          {activeDelTabInfo.delMode === "view" && (
            <>
              <CopyDelButton copyFunction={() => copyDelFunction()} />
              {unapproveDelOption &&
                activeDelTabInfo.currentDel.approved &&
                ((activeDelTabInfo.currentDel.projectInfo.invoiceManager.id &&
                  activeDelTabInfo.currentDel.projectInfo.invoiceManager.id ===
                    currentUser.employee_id) ||
                  currentUser.admin === 1) &&
                activeDelTabInfo.currentDel.canUnapprove === true && (
                  <UnapproveDelButton
                    unapproveFunction={unapproveDelButtonFunction}
                  />
                )}
            </>
          )}

          {activeDelTabInfo.delMode === "approve" && (
            <>
              <RejectDelButton
                rejectFunction={() => rejectDelButtonFunction()}
              />
              <ApproveDelButton
                approveFunction={() => approveDelButtonFunction()}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelForm;
